/**
 * VideoComparison — Side-by-side A/B comparison of dual-pathway video outputs.
 *
 * Option A: Seedance beat-synced clips (stitched)
 * Option B: fal.ai continuous clip
 *
 * Synced playback controls, metadata display, and "Select Winner" for each.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Play, Pause, Trophy, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import Button from '../common/Button';

interface PathwayResult {
  pathway: string;
  success: boolean;
  video_url: string | null;
  provider: string;
  error?: string | null;
}

interface VideoComparisonProps {
  projectId: string;
  optionA: PathwayResult | null;
  optionB: PathwayResult | null;
  audioUrl?: string;
  trimSelection?: { start_time: number; end_time: number; duration: number };
  onSelectWinner?: (option: 'a' | 'b') => void;
  selectedOption?: string | null;
}

export default function VideoComparison({
  projectId: _projectId,
  optionA,
  optionB,
  audioUrl,
  trimSelection,
  onSelectWinner,
  selectedOption,
}: VideoComparisonProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoARef = useRef<HTMLVideoElement>(null!);
  const videoBRef = useRef<HTMLVideoElement>(null!);
  const audioRef = useRef<HTMLAudioElement>(null!);

  // Sync play/pause across both videos and audio
  const handlePlayPause = useCallback(() => {
    const videoA = videoARef.current;
    const videoB = videoBRef.current;
    const audio = audioRef.current;

    if (isPlaying) {
      videoA?.pause();
      videoB?.pause();
      audio?.pause();
      setIsPlaying(false);
    } else {
      // Reset to start for synced playback
      if (videoA) videoA.currentTime = 0;
      if (videoB) videoB.currentTime = 0;
      if (audio) {
        audio.currentTime = trimSelection?.start_time || 0;
      }

      videoA?.play();
      videoB?.play();
      audio?.play();
      setIsPlaying(true);
    }
  }, [isPlaying, trimSelection]);

  // Pause when videos end
  useEffect(() => {
    const videoA = videoARef.current;
    const handleEnded = () => {
      setIsPlaying(false);
      audioRef.current?.pause();
      videoBRef.current?.pause();
    };
    videoA?.addEventListener('ended', handleEnded);
    return () => videoA?.removeEventListener('ended', handleEnded);
  }, []);

  const renderOption = (
    label: string,
    option: PathwayResult | null,
    optionKey: 'a' | 'b',
    videoRef: React.RefObject<HTMLVideoElement>,
  ) => {
    const isWinner = selectedOption === optionKey;

    return (
      <div className={`flex-1 rounded-xl border ${
        isWinner ? 'border-green-500 bg-green-500/5' : 'border-zinc-700 bg-zinc-800/50'
      } overflow-hidden`}>
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
          <div>
            <h4 className="font-medium text-sm">{label}</h4>
            <p className="text-xs text-zinc-400">
              {option?.provider === 'seedance' ? 'Seedance 2.0 (beat-synced)' : 'fal.ai (continuous)'}
            </p>
          </div>
          {isWinner && (
            <div className="flex items-center gap-1 text-green-400">
              <Trophy className="w-4 h-4" />
              <span className="text-xs font-medium">Winner</span>
            </div>
          )}
        </div>

        {/* Video */}
        <div className="aspect-[9/16] bg-black relative max-h-[400px]">
          {option?.success && option.video_url ? (
            <video
              ref={videoRef}
              src={option.video_url}
              className="w-full h-full object-contain"
              muted
              playsInline
            />
          ) : option && !option.success ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-red-400">
              <XCircle className="w-8 h-8 mb-2" />
              <p className="text-sm">Generation Failed</p>
              {option.error && (
                <p className="text-xs text-zinc-500 mt-1 px-4 text-center">{option.error}</p>
              )}
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500">
              <Loader2 className="w-8 h-8 mb-2 animate-spin" />
              <p className="text-sm">Generating...</p>
            </div>
          )}
        </div>

        {/* Select button */}
        {option?.success && option.video_url && !selectedOption && (
          <div className="p-3">
            <Button
              className="w-full"
              variant="secondary"
              onClick={() => onSelectWinner?.(optionKey)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Select {label}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Playback controls */}
      <div className="flex items-center justify-center">
        <button
          onClick={handlePlayPause}
          className="flex items-center gap-2 px-6 py-3 bg-jelly-500 hover:bg-jelly-600 rounded-full transition-colors"
          disabled={!optionA?.video_url && !optionB?.video_url}
        >
          {isPlaying ? (
            <>
              <Pause className="w-5 h-5" />
              <span className="font-medium">Pause Both</span>
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              <span className="font-medium">Play Both</span>
            </>
          )}
        </button>
      </div>

      {/* Side-by-side videos */}
      <div className="flex gap-4">
        {renderOption('Option A', optionA, 'a', videoARef)}
        {renderOption('Option B', optionB, 'b', videoBRef)}
      </div>

      {/* Hidden audio element for synced playback */}
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="auto" />
      )}
    </div>
  );
}
